import FloatingAlert from 'components/Alert/FloatingAlert';
import { useRecoverPassword } from 'hooks/auth/useRecoverPassword';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { requestPassI18n } from './i18n';

const REDIRECT_SECONDS = 7;

const RequestPassChange = ({ DesignSystemComponents }) => {
  const { Card, Input, Button } = DesignSystemComponents;
  const { t } = useScopedTranslation(requestPassI18n);

  const [email, setEmail] = useState('');
  const [alert, setAlert] = useState(null);
  const [isLocked, setIsLocked] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  const timerRef = useRef(null);
  const intervalRef = useRef(null);

  const { recoverPassword, isRecovering } = useRecoverPassword();

  const navigate = useNavigate();

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isRecovering || isLocked) return;

    const normalizedEmail = email.trim();
    if (!normalizedEmail) {
      setAlert({ message: t('rp_err_required'), type: 'danger' });
      return;
    }

    setAlert(null);

    const result = await recoverPassword({ email: normalizedEmail });

    if (result.ok) {
      setIsLocked(true);
      setSecondsLeft(REDIRECT_SECONDS);

      intervalRef.current = setInterval(() => {
        setSecondsLeft((s) => {
          if (s <= 1) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
            return 0;
          }
          return s - 1;
        });
      }, 1000);

      timerRef.current = setTimeout(() => {
        navigate('/login');
      }, REDIRECT_SECONDS * 1000);
    } else {
      setAlert({
        message: t('rp_err_default'),
        type: 'danger',
      });
    }
  };

  useEffect(() => {
    if (!isLocked) return;

    setAlert({
      type: 'success',
      message: t('rp_success', { seconds: secondsLeft || 0 }),
    });
  }, [isLocked, secondsLeft, t]);

  const disabled = isRecovering || isLocked;

  return (
    <div className='min-vh-100 d-flex align-items-center justify-content-center background-cover'>
      <Card className='card-login'>
        <div>
          <div className='text-center mb-6'>
            <p className='caption color-gray-600 mb-2 text-center'>{t('rp_brand')}</p>
            <h1 className='h4 mb-1 text-center'>{t('rp_title')}</h1>
            <p className='caption color-gray-600 mb-0 text-center'>
              {t('rp_subtitle')}
            </p>
          </div>

          <FloatingAlert
            message={alert?.message}
            type={alert?.type || 'danger'}
            onClose={() => setAlert(null)}
          />

          <form
            className='d-flex flex-column'
            onSubmit={handleSubmit}
            noValidate
          >
            <div className=''>
              <Input
                label={t('rp_emailLabel')}
                id='email'
                type='email'
                placeholder={t('rp_emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={disabled}
                required
              />
            </div>
            <div className='d-flex align-items-center justify-content-between mt-3'>
              <Button
                type='submit'
                label={isRecovering ? t('rp_submitting') : t('rp_submit')}
                variant='primary'
                size='sm'
                disabled={disabled}
              />
            </div>
          </form>
        </div>
      </Card>
    </div>
  );
};

export default RequestPassChange;
